import { RootState } from '@/store/store'
import { BillingCycle } from '@/firebase/auth/auth-jwt-schema'
import { getDiscountForAmountDue } from '../utils/discount'

/**
 * Select: Monthly Price for Plan
 *
 * Returns the monthly price (in cents) of a plan for a given billing cycle.
 */
export const selectMonthlyPriceForPlan = (state: RootState, props: { billingCycle: BillingCycle }): number => {
  const { billingCycle } = props
  const { annualPlanMonthlyPrice, monthlyPlanMonthlyPrice } = state.checkoutState

  if (billingCycle === BillingCycle.ANNUAL) {
    return annualPlanMonthlyPrice
  }
  return monthlyPlanMonthlyPrice
}

/**
 * Select: Monthly Plan Annual Cost
 *
 * Calculates the total cost (in cents) over a year if billed monthly.
 */
export const selectMonthlyPlanAnnualCost = (state: RootState): number => {
  const monthlyPrice = selectMonthlyPriceForPlan(state, {
    billingCycle: BillingCycle.MONTHLY,
  })
  return monthlyPrice * 12
}

/**
 * Select: Annual Billing Savings Percent
 *
 * Calculates the percentage saved by choosing annual billing vs monthly for a plan.
 */
export const selectAnnualBillingSavingsPercent = (state: RootState): number => {
  // Cost of monthly plan over a year
  const original = selectMonthlyPlanAnnualCost(state)

  // Return 0 if the original price is 0 (safe guard to avoid division by 0)
  if (original === 0) return 0

  // Cost of annual plan over a year
  const discounted = selectMonthlyPriceForPlan(state, { billingCycle: BillingCycle.ANNUAL }) * 12

  return Math.round(((original - discounted) / original) * 100)
}

/**
 * Select: Amount Due
 *
 * Calculates amount due (in cents) based on the billing cycle and promotion applied.
 */
export const selectAmountDue = (state: RootState): number => {
  const { billingCycle, promotion } = state.checkoutState

  const monthlyPrice = selectMonthlyPriceForPlan(state, { billingCycle })

  const fullPrice = billingCycle === BillingCycle.ANNUAL ? monthlyPrice * 12 : monthlyPrice
  const discount = getDiscountForAmountDue(promotion, monthlyPrice, billingCycle)

  return Math.max(0, fullPrice - discount)
}
